/**
 * Strips share-link access keys out of a URL (or any text carrying one) before
 * it leaves the browser in a RUM event.
 *
 * A replay share id and a natlinkedin link code are bearer credentials: anyone
 * signed in who holds one can open the replay until it expires. RUM records
 * every view URL, referrer and resource URL automatically, so without this the
 * keys would sit in Datadog readable by everyone with RUM access.
 *
 * Each key is replaced by a fixed placeholder rather than dropped, so the views
 * and resources still group under one name (`/replay-share/:shareId`).
 *
 * The paths are literals because a lib may not import a domain. Where each is
 * declared:
 * - `/replay-share/<shareId>` and `/link?ntlkd=<code>`: `campfirePaths` in
 *   `domains/campfire/wehunt-web/src/paths.ts`;
 * - `/api/replay-shares/<shareId>`: campfire's `useSharedHuntReplay`;
 * - `https://share-*.wehunt.app/api/v1/link/<code>` and the share link itself:
 *   the natlinkedin link service, read by campfire's `resolveShareLink`.
 */

const SHARE_ID = ':shareId'
const CODE = ':code'

/**
 * How deep to unwrap a query value that encodes another URL. Two levels cover
 * an sso authorize URL carrying a sign-in URL carrying the returnTo; the cap
 * only exists so a hostile URL cannot make this recurse without end.
 */
const MAX_NESTING = 4

/** One path segment, or one query value, as far as the text around it goes. */
const SEGMENT = String.raw`[^/?#&\s"'<>]+`
const QUERY_VALUE = String.raw`[^&#\s"'<>]+`

const DIRECT_RULES: readonly (readonly [RegExp, string])[] = [
  // The page and its API both: `/replay-share/<id>` and `/replay-shares/<id>`.
  [new RegExp(String.raw`(/replay-shares?/)${SEGMENT}`, 'g'), `$1${SHARE_ID}`],
  [new RegExp(String.raw`(/api/v1/link/)${SEGMENT}`, 'g'), `$1${CODE}`],
  // The share link itself, which a browser can report as the referrer of the
  // `/link` landing it was sent on to. Its API path is handled above.
  [new RegExp(String.raw`(//share-[a-z0-9-]+\.wehunt\.app/)(?!api/)${SEGMENT}`, 'gi'), `$1${CODE}`],
  [new RegExp(String.raw`([?&]ntlkd=)${QUERY_VALUE}`, 'g'), `$1${CODE}`],
]

/** A `key=value` pair in a query string, whose value may encode another URL. */
const QUERY_PAIR = new RegExp(String.raw`([?&][^=&#\s]+=)(${QUERY_VALUE})`, 'g')

export function redactShareSecrets(text: string): string {
  return redact(text, 0)
}

function redact(text: string, depth: number): string {
  const direct = DIRECT_RULES.reduce((out, [pattern, replacement]) => out.replace(pattern, replacement), text)
  if (depth >= MAX_NESTING) return direct
  // A returnTo (or any other parameter) can carry a whole URL percent-encoded,
  // where the slashes the rules above look for are `%2F`. Each value is decoded,
  // redacted as a URL of its own and re-encoded — but only when that changed
  // something, so a clean value keeps its original bytes.
  return direct.replace(QUERY_PAIR, (pair, key: string, value: string) => {
    const decoded = safeDecode(value)
    if (decoded === undefined || decoded === value) return pair
    const redacted = redact(decoded, depth + 1)
    return redacted === decoded ? pair : `${key}${encodeURIComponent(redacted)}`
  })
}

function safeDecode(value: string): string | undefined {
  try {
    return decodeURIComponent(value)
  } catch {
    // A malformed escape cannot hide a key this can find, and the value is
    // passed on exactly as it came rather than losing the event.
    return undefined
  }
}
